import React, { useState, Suspense, lazy } from 'react';
import Window from './Desktop/Window';
import Dock from './Desktop/Dock';
import MusicPlayer from './MusicPlayer';
import VideoPlayer from './VideoPlayer';
import Members from './Members';
import Settings from './Settings';
import WelcomeScreen from './WelcomeScreen';
import DieterRamsClock from './Desktop/DieterRamsClock';

const NewsFeed = lazy(() => import('./NewsFeed'));
const PhotoGallery = lazy(() => import('./PhotoGallery'));
const ArchiveLightTable = lazy(() => import('./Lightbox/ArchiveLightTable'));
const FeedbackWindow = lazy(() => import('./FeedbackWindow'));
const VaultWindow = lazy(() => import('./VaultWindow'));

const DesktopEnvironment = () => {
    const [windows, setWindows] = useState({
        welcome: {
            isOpen: false,
            zIndex: 9999,
            position: {
                x: Math.max(0, window.innerWidth / 2 - 400),
                y: Math.max(0, window.innerHeight / 2 - 300)
            },
            size: { width: 800, height: 600 },
            title: 'Welcome'
        },
        music: { isOpen: true, zIndex: 1, position: { x: 50, y: 120 }, size: { width: 600, height: 560 }, title: 'Paradise FM' },
        video: { isOpen: true, zIndex: 2, position: { x: 670, y: 50 }, size: { width: 480, height: 460 }, title: 'Visuals' },
        images: { isOpen: false, zIndex: 3, position: { x: 100, y: 100 }, size: { width: 600, height: 500 }, title: 'Newsroom' },
        members: {
            isOpen: false,
            zIndex: 4,
            position: {
                x: Math.max(0, window.innerWidth / 2 - 250),
                y: Math.max(0, window.innerHeight / 2 - 300)
            },
            size: { width: 500, height: 660 },
            title: 'ENTER PARADISE'
        },
        settings: { isOpen: false, zIndex: 5, position: { x: 300, y: 200 }, size: { width: 300, height: 400 }, title: 'System Config' },
        photos: { isOpen: false, zIndex: 6, position: { x: 150, y: 150 }, size: { width: 800, height: 600 }, title: 'Photo Gallery' },
        archive: { isOpen: false, zIndex: 7, position: { x: 100, y: 100 }, size: { width: 900, height: 700 }, title: 'Light Table' }, // Resized for Canvas
        feedback: { isOpen: false, zIndex: 8, position: { x: 400, y: 300 }, size: { width: 400, height: 500 }, title: 'Member Feedback' },
        vault: { isOpen: false, zIndex: 9, position: { x: 250, y: 250 }, size: { width: 800, height: 500 }, title: 'Secure Vault' },
    });
    const [activeWindow, setActiveWindow] = useState(null);
    const [maxZIndex, setMaxZIndex] = useState(3);
    const [user, setUser] = useState(null); // { id, email, isSubscribed }
    const [hasNewNews, setHasNewNews] = useState(false);
    const [latestNewsDate, setLatestNewsDate] = useState(null);

    const handleNewsContentLoaded = (latestDate) => {
        setLatestNewsDate(latestDate);
        const lastSeen = localStorage.getItem('paradise_last_seen_news');
        if (!lastSeen || new Date(latestDate) > new Date(lastSeen)) {
            setHasNewNews(true);
        }
    };

    // When Newsroom window opens, mark news as seen
    React.useEffect(() => {
        if (windows.images?.isOpen && latestNewsDate) {
            setHasNewNews(false);
            localStorage.setItem('paradise_last_seen_news', latestNewsDate);
        }
    }, [windows.images?.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const hasVisited = localStorage.getItem('paradise_welcome_seen');
        if (!hasVisited) {
            setWindows(prev => ({
                ...prev,
                welcome: { ...prev.welcome, isOpen: true, zIndex: 9999 },
                video: { ...prev.video, isOpen: false } // Hide video initially if welcome is shown
            }));
            setActiveWindow('welcome');
        }

        const storedUser = localStorage.getItem('paradise_user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);


                // ALWAYS refresh status to ensure tier is up to date (sync with Stripe)
                if (parsedUser.id) {
                    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
                    const storedToken = localStorage.getItem('paradise_token');
                    if (!storedToken) return;
                    fetch(`${API_BASE}/api/auth/status`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${storedToken}`
                        },
                        body: JSON.stringify({ userId: parsedUser.id })
                    })
                        .then(res => res.json())
                        .then(data => {
                            if (data.user) {
                                console.log('[Desktop] Refreshed user status:', data.user);
                                setUser(data.user);
                                localStorage.setItem('paradise_user', JSON.stringify(data.user)); // Sync local storage

                                // If this was a checkout success redirect, open members window
                                const params = new URLSearchParams(window.location.search);
                                if (params.get('success') === 'true') {
                                    setWindows(prev => ({
                                        ...prev,
                                        members: { ...prev.members, isOpen: true, zIndex: 9999 }
                                    }));
                                    setActiveWindow('members');

                                    // Clean URL
                                    const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
                                    window.history.replaceState({ path: newUrl }, '', newUrl);
                                }
                            }
                        })
                        .catch(err => console.error("Failed to refresh user status", err));
                }

            } catch (e) {
                console.error("Failed to parse stored user", e);
            }
        }

        // Logic for token login (reset password link etc)
        const params = new URLSearchParams(window.location.search);
        if (params.get('token')) {
            setWindows(prev => ({
                ...prev,
                members: { ...prev.members, isOpen: true, zIndex: 10 }
            }));
            setActiveWindow('members');
        }
    }, []);

    const openWindow = (id) => {
        if (!id) return;
        setWindows(prev => ({
            ...prev,
            [id]: { ...prev[id], isOpen: true, zIndex: maxZIndex + 1 }
        }));
        setMaxZIndex(prev => prev + 1);
        setActiveWindow(id);
    };

    const closeWindow = (id) => {
        setWindows(prev => ({
            ...prev,
            [id]: { ...prev[id], isOpen: false }
        }));
    };

    const focusWindow = (id) => {
        if (activeWindow === id) return;
        setWindows(prev => ({
            ...prev,
            [id]: { ...prev[id], zIndex: maxZIndex + 1 }
        }));
        setMaxZIndex(prev => prev + 1);
        setActiveWindow(id);
    };

    const resizeWindow = (id, newSize) => {
        setWindows(prev => ({
            ...prev,
            [id]: { ...prev[id], size: { ...prev[id].size, ...newSize } }
        }));
    };

    const handleMinimize = (id) => {
        setWindows(prev => {
            const windowState = prev[id];
            if (!windowState) return prev;

            const isMini = !windowState.isMini; // Toggle
            let newSize = windowState.size;

            // Specific Logic for Music Player
            if (id === 'music') {
                newSize = isMini ? { width: 320, height: 160 } : { width: 600, height: 560 };
            }

            return {
                ...prev,
                [id]: {
                    ...windowState,
                    isMini: isMini,
                    size: newSize
                }
            };
        });
    };

    const constraintsRef = React.useRef(null);

    const [currentTheme, setCurrentTheme] = useState('paradise');

    const themes = {
        paradise: '#e6e2d3',
        lagoon: '#b8e2f2',
        sunset: '#f5d0b5',
        jungle: '#dbe7c9'
    };

    const canSubmitFeedback = user && (user.membershipTier === 'club' || user.membershipTier === 'executive' || user.isPhotographer);

    return (
        <div ref={constraintsRef} className="relative w-full h-screen overflow-hidden font-sans selection:bg-gray-700 selection:text-white" style={{ backgroundColor: themes[currentTheme] }}>
            {/* Background Pattern */}
            <div className="absolute inset-0 pointer-events-none opacity-10 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[length:20px_20px]"></div>



            {/* Dieter Rams Clock */}
            <DieterRamsClock />

            {/* Top Left Menu: Become a Member / Creator */}
            {!user && (
                <div className="absolute top-6 left-6 z-40 flex items-center gap-3">
                    <button
                        onClick={() => openWindow('members')}
                        className="flex items-center bg-[#e8e6df] text-[#444444] px-4 py-2 rounded shadow-lg hover:bg-[#dcdad3] active:scale-95 transition-all border border-[#d1d1d1]"
                    >
                        <span className="text-[10px] font-bold tracking-widest uppercase text-[#444444]">BECOME A MEMBER</span>
                    </button>
                    <button
                        onClick={() => window.location.href = '/apply'}
                        className="flex items-center bg-[#222] text-white px-4 py-2 rounded shadow-lg hover:bg-black active:scale-95 transition-all border border-black"
                    >
                        <span className="text-[10px] font-bold tracking-widest uppercase">BECOME A CREATOR</span>
                    </button>
                </div>
            )}


            {/* Windows */}
            {windows.welcome?.isOpen && (
                <Window
                    id="welcome"
                    title={windows.welcome.title}
                    isOpen={windows.welcome.isOpen}
                    onClose={(id) => {
                        closeWindow(id);
                        localStorage.setItem('paradise_welcome_seen', 'true');
                        // Open video if it was closed
                        setWindows(prev => ({
                            ...prev,
                            video: { ...prev.video, isOpen: true }
                        }));
                    }}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.welcome.zIndex}
                    position={windows.welcome.position}
                    size={windows.welcome.size}
                    constraintsRef={constraintsRef}
                >
                    <WelcomeScreen
                        onClose={() => {
                            closeWindow('welcome');
                            localStorage.setItem('paradise_welcome_seen', 'true');
                            setWindows(prev => ({
                                ...prev,
                                video: { ...prev.video, isOpen: true }
                            }));
                        }}
                    />
                </Window>
            )}

            {windows.music.isOpen && (
                <Window
                    id="music"
                    title={windows.music.title}
                    isOpen={windows.music.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    onMinimize={handleMinimize}
                    zIndex={windows.music.zIndex}
                    position={windows.music.position}
                    size={windows.music.size}
                    constraintsRef={constraintsRef}
                >
                    <MusicPlayer isMini={windows.music.isMini} />
                </Window>
            )}
            {/* Video Player Window */}
            {windows.video.isOpen && (
                <Window
                    id="video"
                    title={windows.video.title}
                    isOpen={windows.video.isOpen}
                    onClose={closeWindow}
                    zIndex={windows.video.zIndex}
                    onFocus={focusWindow}
                    position={windows.video.position}
                    size={windows.video.size}
                    onResize={resizeWindow}
                    constraintsRef={constraintsRef}
                    scrollable={false} // Video player handles its own sizing
                >
                    <VideoPlayer isActive={true} user={user} onOpenMembers={() => openWindow('members')} />
                </Window>
            )}

            {/* Image Viewer Window (PDFs) */}
            {windows.images.isOpen && (
                <Window
                    id="images"
                    title={windows.images.title}
                    isOpen={windows.images.isOpen}
                    onClose={closeWindow}
                    zIndex={windows.images.zIndex}
                    onFocus={focusWindow}
                    position={windows.images.position}
                    size={windows.images.size}
                    onResize={resizeWindow}
                    constraintsRef={constraintsRef}
                    scrollable={false} // Image viewer handles its own scrolling
                >
                    <Suspense fallback={<div className="p-4 text-xs font-mono">Loading Feed...</div>}>
                        <NewsFeed onContentLoaded={handleNewsContentLoaded} />
                    </Suspense>
                </Window>
            )}

            {windows.members.isOpen && (
                <Window
                    id="members"
                    title={windows.members.title}
                    isOpen={windows.members.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.members.zIndex}
                    position={windows.members.position}
                    size={windows.members.size}
                    constraintsRef={constraintsRef}
                >
                    <Members user={user} setUser={setUser} onOpenArchive={() => openWindow('archive')} />
                </Window>
            )}

            {windows.settings?.isOpen && (
                <Window
                    id="settings"
                    title={windows.settings.title}
                    isOpen={windows.settings.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.settings.zIndex}
                    position={windows.settings.position}
                    size={windows.settings.size}
                    constraintsRef={constraintsRef}
                >
                    <Settings
                        currentTheme={currentTheme}
                        onThemeChange={setCurrentTheme}
                        onOpenFeedback={canSubmitFeedback ? () => openWindow('feedback') : null}
                    />
                </Window>
            )}

            {windows.photos?.isOpen && (
                <Window
                    id="photos"
                    title={windows.photos.title}
                    isOpen={windows.photos.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.photos.zIndex}
                    position={windows.photos.position}
                    size={windows.photos.size}
                    constraintsRef={constraintsRef}
                >
                    <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-xs font-mono text-gray-500">LOADING MODULE...</div>}>
                        <PhotoGallery user={user} onOpenMembers={() => openWindow('members')} />
                    </Suspense>
                </Window>
            )}

            {windows.archive?.isOpen && (
                <Window
                    id="archive"
                    title={windows.archive.title}
                    isOpen={windows.archive.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.archive.zIndex}
                    position={windows.archive.position}
                    size={windows.archive.size}
                    constraintsRef={constraintsRef}
                    scrollable={false} // Disable window scroll, let canvas handle it
                >
                    <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-xs font-mono text-gray-500">SETTING UP TABLE...</div>}>
                        <ArchiveLightTable user={user} />
                    </Suspense>
                </Window>
            )}

            {/* Feedback Window */}
            {windows.feedback?.isOpen && (
                <Window
                    id="feedback"
                    title={windows.feedback.title}
                    isOpen={windows.feedback.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.feedback.zIndex}
                    position={windows.feedback.position}
                    size={windows.feedback.size}
                    constraintsRef={constraintsRef}
                >
                    <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-xs font-mono text-gray-500">LOADING...</div>}>
                        <FeedbackWindow user={user} onClose={() => closeWindow('feedback')} />
                    </Suspense>
                </Window>
            )}

            {/* Vault Window */}
            {windows.vault?.isOpen && (
                <Window
                    id="vault"
                    title={windows.vault.title}
                    isOpen={windows.vault.isOpen}
                    onClose={closeWindow}
                    onFocus={focusWindow}
                    onResize={resizeWindow}
                    zIndex={windows.vault.zIndex}
                    position={windows.vault.position}
                    size={windows.vault.size}
                    constraintsRef={constraintsRef}
                >
                    <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-xs font-mono text-gray-500">AUTHENTICATING...</div>}>
                        <VaultWindow user={user} />
                    </Suspense>
                </Window>
            )}



            {/* Dock */}
            <Dock onOpenWindow={openWindow} user={user} hasNewNews={hasNewNews} />
        </div>
    );
};

export default DesktopEnvironment;
